// M-Bus and KNX managed from central (ADR 0008): response shapes of
// `/api/v1/edges/:id/mbus`, `/knx` and `/knx/dpts`. Like types.ts: zod/mini,
// not strict, snake_case, UTC epoch ms. The drafts are the config bundle
// sections (`@kalvio/contracts` config-bundle.ts), duplicated here so the
// bundle does not pull in the contracts package.
import { z } from 'zod/mini';
import { discoveryRunState } from './types.js';

const nullableNumber = z.nullable(z.number());
const nullableString = z.nullable(z.string());

export type ProtocolName = 'mbus' | 'knx';

/** A discovery run of the protocol (M-Bus scan, KNX seen report). */
export const protocolRun = z.object({
  id: z.string(),
  protocol: z.string(),
  scope: z.nullable(z.record(z.string(), z.unknown())),
  state: discoveryRunState,
  item_count: z.number(),
  error: nullableString,
  created_at: z.number(),
  started_at: nullableNumber,
  finished_at: nullableNumber,
});
export type ProtocolRun = z.infer<typeof protocolRun>;
export const protocolRunResponse = z.object({ run: protocolRun });

/** Where a draft stands against the config bundles. */
export const bundleState = z.object({
  /** The draft differs from the newest bundle: Apply sends it. */
  pending: z.boolean(),
  /** Newest bundle that was not rejected (also the BACnet `config_version`). */
  version: nullableNumber,
  status: z.nullable(z.enum(['sent', 'applied', 'rejected', 'pending'])),
  /** That bundle carries the protocol's section (central manages it on the edge). */
  section_in_bundle: z.boolean(),
  applied_version: nullableNumber,
  /** A newer bundle the edge rejected, with its reasons. */
  rejected: z.nullable(z.object({ version: z.number(), error: nullableString })),
});
export type BundleState = z.infer<typeof bundleState>;

/** A protocol point central knows (for live values and enabled state). */
export const protocolPoint = z.object({
  device_id: z.string(),
  metric: z.string(),
  point_id: z.string(),
  enabled: z.boolean(),
});
export type ProtocolPoint = z.infer<typeof protocolPoint>;

// --- M-Bus ----------------------------------------------------------------------

export const MBUS_BAUD_RATES = [300, 600, 1200, 2400, 4800, 9600, 19200, 38400] as const;

export const mbusMeter = z.object({
  device_id: z.string(),
  /** Primary address 0–250; null = secondary addressing. */
  primary: nullableNumber,
  name: nullableString,
});
export type MbusMeter = z.infer<typeof mbusMeter>;

export const mbusConverter = z.object({
  id: z.string(),
  host: z.string(),
  port: z.number(),
  baud_rate: z.number(),
  response_timeout_ms: nullableNumber,
  meters: z.array(mbusMeter),
});
export type MbusConverter = z.infer<typeof mbusConverter>;

export const mbusPoint = z.object({
  point_key: z.string(),
  device_id: z.string(),
  metric: z.string(),
  mode: z.string(),
  interval_ms: nullableNumber,
  object_name: z.string(),
  description: z.string(),
  units: z.string(),
});
export type MbusPoint = z.infer<typeof mbusPoint>;

export const mbusRecord = z.object({
  metric: z.string(),
  quantity: z.string(),
  description: z.string(),
  unit: z.string(),
  data_type: z.string(),
  value: z.union([z.number(), z.string(), z.null()]),
  storage: z.number(),
  tariff: z.number(),
  subunit: z.number(),
  function: z.string(),
  error: nullableString,
});
export type MbusRecord = z.infer<typeof mbusRecord>;

/** A meter found by a scan (newest scan per meter) with its records. */
export const mbusScannedMeter = z.object({
  device_id: z.string(),
  converter: z.string(),
  primary_address: nullableNumber,
  secondary: z.nullable(
    z.object({
      id: z.string(),
      manufacturer: z.string(),
      version: z.number(),
      medium: z.number(),
    }),
  ),
  medium_text: nullableString,
  status_text: z.string(),
  record_count: z.number(),
  errors: z.array(z.string()),
  error: nullableString,
  scanned_at: z.number(),
  records: z.array(mbusRecord),
});
export type MbusScannedMeter = z.infer<typeof mbusScannedMeter>;

export const mbusState = z.object({
  edge: z.object({ id: z.string(), edge_node_id: z.string(), site_id: z.string() }),
  /** Central manages M-Bus on this edge (else the edge's local file does). */
  managed: z.boolean(),
  /** Draft revision; send it back as `base_revision` / `revision`. */
  revision: nullableNumber,
  updated_at: nullableNumber,
  draft: z.object({ converters: z.array(mbusConverter), points: z.array(mbusPoint) }),
  bundle: bundleState,
  scans: z.array(protocolRun),
  meters: z.array(mbusScannedMeter),
  scan_failures: z.array(
    z.object({
      converter: z.string(),
      primary_address: nullableNumber,
      error: nullableString,
      scanned_at: z.number(),
    }),
  ),
  points: z.array(protocolPoint),
});
export type MbusState = z.infer<typeof mbusState>;

export interface ConverterBody {
  host: string;
  port: number;
  baud_rate: number;
  response_timeout_ms: number | null;
  base_revision?: number | null;
}

export interface MeterBody {
  converter_id: string;
  primary: number | null;
  name: string | null;
  records: { metric: string; interval_ms: number | null }[];
  base_revision?: number | null;
}

export interface ScanBody {
  converter_id: string;
  mode: 'primary' | 'secondary';
  primary_from?: number;
  primary_to?: number;
  mask?: string;
}

// --- KNX ------------------------------------------------------------------------

export const knxGroupAddress = z.object({
  address: z.string(),
  dpt: z.string(),
  name: nullableString,
  description: nullableString,
  unit: nullableString,
});
export type KnxGroupAddress = z.infer<typeof knxGroupAddress>;

export const knxInterface = z.object({
  id: z.string(),
  mode: z.enum(['routing', 'tunnelling']),
  host: nullableString,
  port: z.number(),
  multicast_group: nullableString,
  local_address: nullableString,
  nat: z.boolean(),
  group_addresses: z.array(knxGroupAddress),
});
export type KnxInterface = z.infer<typeof knxInterface>;

export const knxSeenAddress = z.object({
  device_id: z.string(),
  interface: z.string(),
  address: z.string(),
  telegrams: z.number(),
  first_ts: z.number(),
  last_ts: z.number(),
  last_service: z.string(),
  last_source: z.string(),
  last_raw: z.string(),
});
export type KnxSeenAddress = z.infer<typeof knxSeenAddress>;

export const knxState = z.object({
  edge: z.object({ id: z.string(), edge_node_id: z.string(), site_id: z.string() }),
  managed: z.boolean(),
  revision: nullableNumber,
  updated_at: nullableNumber,
  draft: z.object({ interfaces: z.array(knxInterface) }),
  bundle: bundleState,
  seen: z.object({
    /** The newest report request (may still run). */
    run: z.nullable(protocolRun),
    reported_at: nullableNumber,
    items: z.array(knxSeenAddress),
  }),
  points: z.array(protocolPoint),
});
export type KnxState = z.infer<typeof knxState>;

export const knxDpt = z.object({
  id: z.string(),
  name: z.string(),
  unit: z.string(),
  data_type: z.string(),
});
export type KnxDpt = z.infer<typeof knxDpt>;
export const knxDptList = z.object({ items: z.array(knxDpt) });

export interface InterfaceBody {
  mode: 'routing' | 'tunnelling';
  host: string | null;
  port: number;
  multicast_group: string | null;
  local_address: string | null;
  nat: boolean;
  base_revision?: number | null;
}

export interface GroupAddressBody {
  address: string;
  dpt: string;
  name?: string | null;
  description?: string | null;
  unit?: string | null;
}

export type EtsRowStatus = 'new' | 'changed' | 'same' | 'unsupported';

export const etsPreview = z.object({
  revision: nullableNumber,
  format: z.enum(['xml', 'csv']),
  counts: z.object({
    total: z.number(),
    new: z.number(),
    changed: z.number(),
    same: z.number(),
    unsupported: z.number(),
  }),
  rows: z.array(
    z.object({
      address: z.string(),
      name: z.string(),
      description: z.string(),
      dpt: nullableString,
      path: z.array(z.string()),
      status: z.enum(['new', 'changed', 'same', 'unsupported']),
      current: z.nullable(knxGroupAddress),
    }),
  ),
  warnings: z.array(z.string()),
});
export type EtsPreview = z.infer<typeof etsPreview>;
export type EtsPreviewRow = EtsPreview['rows'][number];

export interface EtsUpload {
  /** File content as base64 (any encoding the ETS export uses). */
  content: string;
  encoding: 'base64' | 'utf8';
  filename?: string;
}

export const etsImportResult = z.object({
  revision: z.number(),
  added: z.number(),
  updated: z.number(),
  skipped: z.number(),
});
export type EtsImportResult = z.infer<typeof etsImportResult>;

// --- shared ---------------------------------------------------------------------

export const revisionResult = z.object({ revision: z.number() });

export const applyResult = z.object({
  version: z.number(),
  status: z.string(),
  unchanged: z.boolean(),
});
export type ApplyResult = z.infer<typeof applyResult>;
